/**
 * Builds the linear, one-question-at-a-time walk for the remote adaptive
 * screener from the server-composed section list, the resolved Questionnaires,
 * and the answers saved so far. The server fixes which sections are present
 * (from cross-visit memory); this module applies the *within-visit* short-check
 * gate so the full anxiety instrument (GAD-7) is only administered when the
 * brief check (GAD-2) is positive. All other sections render in full.
 * @agentic members
 */

import type { Questionnaire, QuestionnaireItem } from "@medplum/fhirtypes";
import {
  LIKERT_FREQUENCY_OPTIONS,
  QUESTIONNAIRE_URLS,
  getQuestionnaireItems,
  type QuestionItem,
} from "@benchmark/core/constants";
import { shouldRevealAnxietyFullSetFromState } from "@benchmark/core/services";
import {
  flattenQuestionnaireItems,
  getSectionLabelForUrl,
  getInstructionsForUrl,
} from "../surveyFlow";
import type { ScreenerSection } from "./api";

/** One question step in the screener walk. */
export type ScreenerStep = {
  questionnaireUrl: string;
  sectionKey: string;
  sectionLabel: string;
  instructions: string | undefined;
  item: QuestionnaireItem;
  questionOrdinal: number;
  totalQuestions: number;
};

/** Answers keyed by questionnaire URL then linkId → selected option code. */
export type ScreenerAnswers = Record<string, Record<string, string>>;

/** Explicitly skipped link ids keyed by questionnaire URL (§8.4). */
export type ScreenerSkips = Record<string, readonly string[]>;

/**
 * Every instrument URL belonging to a section with ANY explicit settlement: a
 * decline settles the whole section (D10/§9.15), so its other instruments —
 * including an already-completed short check — must also leave the walk (no
 * step, no Back target, no resume into it). The settlements map is the
 * server's; this only expands it to the section boundary for walk rendering.
 */
export const settledSectionInstrumentUrls = (
  sections: ScreenerSection[],
  settlements: Record<string, string>,
): Set<string> => {
  const settled = new Set<string>();
  for (const section of sections) {
    if (
      section.instruments.some(
        (instrument) => settlements[instrument.questionnaireUrl] !== undefined,
      )
    ) {
      for (const instrument of section.instruments) {
        settled.add(instrument.questionnaireUrl);
      }
    }
  }
  return settled;
};

const LIKERT_CODE_TO_WEIGHT = new Map<string, number>(
  LIKERT_FREQUENCY_OPTIONS.map((option) => [option.coding.code, option.value]),
);

/**
 * Numeric weight of a stored answer code. Answer rows hold the selected
 * option's `code`, which is a numeric string on the canonical questionnaires
 * but a LOINC LA code (e.g. `LA6568-5`) on responses saved under
 * Medplum-authored forms — decoding with `Number(code)` alone silently breaks
 * the in-instrument gate and the GAD-2 total for those. Mirrors the server's
 * decode (`numericAnswerValue` in `@benchmark/core`).
 */
const answerCodeWeight = (code: string | undefined): number | undefined => {
  if (code === undefined) return undefined;
  const weight = LIKERT_CODE_TO_WEIGHT.get(code);
  if (weight !== undefined) return weight;
  const parsed = Number(code);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/** Flatten the canonical (core) question items, descending into groups. */
const flattenQuestionItems = (items: readonly QuestionItem[]): QuestionItem[] =>
  items.flatMap((item) =>
    item.type === "group" && item.item
      ? [item, ...flattenQuestionItems(item.item)]
      : [item],
  );

/**
 * Link ids hidden by item-level branching for one instrument: a `gateFor`
 * parent answered 0 (e.g. AUDIT-C "Never") transitively hides its linked
 * subtree. Mirrors the core completion gate (`evaluateQuestionnaireCompletion`)
 * so the walk, the completeness check, and scoring agree on which items are
 * active — an alcohol short-check that answers "Never" finishes at a true 0
 * without administering (or persisting) items 2–3.
 */
const gatedHiddenLinkIds = (
  questionnaireUrl: string,
  answers: ScreenerAnswers,
): Set<string> => {
  const items = getQuestionnaireItems(questionnaireUrl);
  const hidden = new Set<string>();
  if (!items) return hidden;
  const flattened = flattenQuestionItems(items);
  const gateForByLinkId = new Map<string, readonly string[]>();
  for (const item of flattened) {
    if (item.gateFor) gateForByLinkId.set(item.linkId, item.gateFor);
  }
  const row = answers[questionnaireUrl] ?? {};
  for (const item of flattened) {
    if (!item.gateFor) continue;
    const value = answerCodeWeight(row[item.linkId]);
    if (value === undefined || value !== 0) continue;
    const queue = [...item.gateFor];
    while (queue.length > 0) {
      const id = queue.shift();
      if (id === undefined || hidden.has(id)) continue;
      hidden.add(id);
      queue.push(...(gateForByLinkId.get(id) ?? []));
    }
  }
  return hidden;
};

const instrumentTotal = (
  questionnaireUrl: string,
  questionnaires: Map<string, Questionnaire>,
  answers: ScreenerAnswers,
): number => {
  const q = questionnaires.get(questionnaireUrl);
  if (!q) return 0;
  const row = answers[questionnaireUrl] ?? {};
  return flattenQuestionnaireItems(q.item).reduce((sum, item) => {
    const value = answerCodeWeight(row[item.linkId ?? ""]);
    return value !== undefined ? sum + value : sum;
  }, 0);
};

/**
 * Which instrument URLs of a section are administered this visit, applying the
 * anxiety short-check gate.
 */
const instrumentsForSectionMap = (
  section: ScreenerSection,
  questionnaires: Map<string, Questionnaire>,
  answers: ScreenerAnswers,
): string[] => {
  const urls = section.instruments.map((i) => i.questionnaireUrl);
  if (section.key !== "anxiety") {
    return urls;
  }
  // full-by-memory: the full instrument was opened at an earlier visit, so
  // administer GAD-7 directly and skip the GAD-2 check (memory only adds).
  if (section.gating === "full-by-memory") {
    return urls.filter((url) => url === QUESTIONNAIRE_URLS.GAD7);
  }
  // short-check: GAD-2 always; GAD-7 once GAD-2 is positive OR GAD-7 already
  // holds any answer (owner rule W1: GAD-7 is retained once answered, even if
  // GAD-2 is later edited below the cutoff — the server applies the same rule).
  const revealFull = shouldRevealAnxietyFullSetFromState({
    gad2Score: instrumentTotal(
      QUESTIONNAIRE_URLS.GAD2,
      questionnaires,
      answers,
    ),
    hasGad7Responses:
      Object.keys(answers[QUESTIONNAIRE_URLS.GAD7] ?? {}).length > 0,
  });
  return urls.filter((url) => url !== QUESTIONNAIRE_URLS.GAD7 || revealFull);
};

/**
 * Build the ordered question walk for the current answers. The list is
 * recomputed as answers change so the anxiety gate opens/closes GAD-7 live.
 * Instruments with an explicit settlement (a declined section, per the
 * server's `settlements` map) produce no steps — a settled section is never
 * re-offered (D10/§9.15); skipped items stay in the walk (they are presented
 * positions, reachable via the one-step-back, §8.16).
 * @agentic members
 */
export const buildScreenerSteps = (
  sections: ScreenerSection[],
  questionnaires: Questionnaire[],
  answers: ScreenerAnswers,
  settledUrls?: ReadonlySet<string>,
): ScreenerStep[] => {
  const byUrl = new Map<string, Questionnaire>();
  for (const q of questionnaires) {
    if (q.url) byUrl.set(q.url, q);
  }

  const steps: Omit<ScreenerStep, "questionOrdinal" | "totalQuestions">[] = [];
  for (const section of sections) {
    for (const url of instrumentsForSectionMap(section, byUrl, answers)) {
      if (settledUrls?.has(url)) continue;
      const q = byUrl.get(url);
      if (!q) continue;
      const sectionLabel = getSectionLabelForUrl(url);
      const instructions = getInstructionsForUrl(url);
      const hidden = gatedHiddenLinkIds(url, answers);
      for (const item of flattenQuestionnaireItems(q.item)) {
        if (item.linkId && hidden.has(item.linkId)) continue;
        steps.push({
          questionnaireUrl: url,
          sectionKey: section.key,
          sectionLabel,
          instructions,
          item,
        });
      }
    }
  }

  const totalQuestions = steps.length;
  return steps.map((step, index) => ({
    ...step,
    questionOrdinal: index + 1,
    totalQuestions,
  }));
};

/** All flattened items of an instrument, for full-instrument completeness. */
export const instrumentItems = (
  questionnaireUrl: string,
  questionnaires: Questionnaire[],
): QuestionnaireItem[] => {
  const q = questionnaires.find((it) => it.url === questionnaireUrl);
  return q ? flattenQuestionnaireItems(q.item) : [];
};

/**
 * The items of an instrument that are active given the current answers —
 * excluding any hidden by item-level branching (e.g. AUDIT-C items 2–3 after a
 * "Never"). Completeness, the answer payload, and scoring must all count only
 * active items so a gated short-check reads as complete at its true score.
 */
export const activeInstrumentItems = (
  questionnaireUrl: string,
  questionnaires: Questionnaire[],
  answers: ScreenerAnswers,
): QuestionnaireItem[] => {
  const hidden = gatedHiddenLinkIds(questionnaireUrl, answers);
  return instrumentItems(questionnaireUrl, questionnaires).filter(
    (item) => !(item.linkId && hidden.has(item.linkId)),
  );
};

/**
 * The instrument URLs a section administers this visit (after the anxiety
 * short-check gate), regardless of decline settlement — the set the results
 * derivation reasons about.
 */
export const administeredInstrumentUrls = (
  section: ScreenerSection,
  questionnaires: Questionnaire[],
  answers: ScreenerAnswers,
): string[] => {
  const byUrl = new Map<string, Questionnaire>();
  for (const q of questionnaires) {
    if (q.url) byUrl.set(q.url, q);
  }
  return instrumentsForSectionMap(section, byUrl, answers);
};

/** Whether an instrument has at least one active answered item. */
const instrumentHasAnswer = (
  url: string,
  questionnaires: Questionnaire[],
  answers: ScreenerAnswers,
): boolean => {
  const row = answers[url] ?? {};
  return activeInstrumentItems(url, questionnaires, answers).some(
    (item) => row[item.linkId ?? ""] !== undefined,
  );
};

/** Whether every active item of an instrument is answered. */
const instrumentComplete = (
  url: string,
  questionnaires: Questionnaire[],
  answers: ScreenerAnswers,
): boolean => {
  const items = activeInstrumentItems(url, questionnaires, answers);
  const row = answers[url] ?? {};
  return (
    items.length > 0 && items.every((it) => row[it.linkId ?? ""] !== undefined)
  );
};

/** A section's results resolution: whether it is complete and its scoring URL. */
export type ScreenerSectionResult = {
  /** True when every administered instrument in the section is fully answered. */
  complete: boolean;
  /** The instrument whose completed answers carry the band, when complete. */
  scoreUrl?: string;
  /** True when at least one administered instrument has an answer. */
  anyAnswered: boolean;
};

/**
 * FALLBACK-ONLY two-state resolution (D3/§8.9), kept solely for the
 * pre-lifecycle-server path: when a state response carries no `sectionStates`
 * map (a rollback), the readback derives complete-vs-not locally so results
 * never blank. Whenever `sectionStates` is present, the readback renders the
 * SERVER's recorded state and this function is not consulted (D18).
 */
export const deriveSectionResult = (
  section: ScreenerSection,
  questionnaires: Questionnaire[],
  answers: ScreenerAnswers,
): ScreenerSectionResult => {
  const urls = administeredInstrumentUrls(section, questionnaires, answers);
  const anyAnswered = urls.some((url) =>
    instrumentHasAnswer(url, questionnaires, answers),
  );
  const allComplete =
    urls.length > 0 &&
    urls.every((url) => instrumentComplete(url, questionnaires, answers));
  if (allComplete) {
    // The scoring instrument is the last administered one (GAD-7 over GAD-2).
    return { complete: true, scoreUrl: urls[urls.length - 1], anyAnswered };
  }
  return { complete: false, anyAnswered };
};

/**
 * Index of the first never-presented step — neither answered nor explicitly
 * skipped (§8.11): resume never re-offers a skipped item (§8.4); the
 * one-step-back within the sitting remains the only way back onto one (§8.16).
 */
export const firstUnpresentedStepIndex = (
  steps: ScreenerStep[],
  answers: ScreenerAnswers,
  skips: ScreenerSkips,
): number => {
  const idx = steps.findIndex((step) => {
    const linkId = step.item.linkId ?? "";
    const code = answers[step.questionnaireUrl]?.[linkId];
    if (code !== undefined && code !== "") return false;
    return !(skips[step.questionnaireUrl] ?? []).includes(linkId);
  });
  return idx === -1 ? Math.max(0, steps.length - 1) : idx;
};
